import { EMPTY, map, merge, Observable, of, switchMap } from 'rxjs';
import { Pub, State, Sub } from '../../../../../../shared/state/state.abstract.ts';
import { responseHandler } from '../../../../../../shared/responseHandler/responseHandler.ts';
import { orderService } from '../../../../services/order.service.ts';
import {
  getDefaultComparisonDate,
  IComparisonMetricsByKey,
  MAX_COMPARISON_DATES,
} from './comparison.types.ts';

export interface IComparisonMetricsState {
  action:
    | 'toggleVisible'
    | 'addDate'
    | 'changeDate'
    | 'removeDate'
    | 'setContext'
    | 'fetched'
    | 'reset'
    | undefined;
  visible: boolean;
  dates: string[];
  metricsByKey: IComparisonMetricsByKey;
  customerId?: string;
  orderDate?: string;
  articleIds: string[];
}

export const initComparisonMetricsState: IComparisonMetricsState = {
  action: undefined,
  visible: false,
  dates: [],
  metricsByKey: {},
  customerId: undefined,
  orderDate: undefined,
  articleIds: [],
};

const sameIds = (a: string[], b: string[]): boolean =>
  a.length === b.length && [...a].sort().join(',') === [...b].sort().join(',');

class PubImpl extends Pub<IComparisonMetricsState> {
  setContext(params: { customerId?: string; orderDate?: string; articleIds: string[] }) {
    const s = this.stream$.getValue();
    const ctxChanged = s.customerId !== params.customerId || s.orderDate !== params.orderDate;
    const idsChanged = !sameIds(s.articleIds, params.articleIds);
    if (!ctxChanged && !idsChanged) return;
    // a different customer invalidates the keyed metrics, so drop them up front
    const metricsByKey = s.customerId === params.customerId ? s.metricsByKey : {};
    this.emit('setContext', { ...params, metricsByKey });
  }
  toggleVisible() {
    const s = this.stream$.getValue();
    const visible = !s.visible;
    let dates = s.dates;
    if (visible && !dates.length) {
      const def = getDefaultComparisonDate(s.orderDate);
      if (def) dates = [def];
    }
    this.emit('toggleVisible', { visible, dates });
  }
  addDate(date?: string) {
    const s = this.stream$.getValue();
    if (s.dates.length >= MAX_COMPARISON_DATES) return;
    const newDate = date || getDefaultComparisonDate(s.orderDate);
    if (!newDate || s.dates.includes(newDate)) return;
    this.emit('addDate', { dates: [...s.dates, newDate], visible: true });
  }
  changeDate(oldDate: string, newDate: string) {
    const s = this.stream$.getValue();
    if (s.dates.includes(newDate)) return;
    this.emit('changeDate', { dates: s.dates.map((d) => (d === oldDate ? newDate : d)) });
  }
  removeDate(date: string) {
    const s = this.stream$.getValue();
    const dates = s.dates.filter((d) => d !== date);
    // collapse back to the off-state once the last date is removed
    this.emit('removeDate', { dates, visible: dates.length > 0 && s.visible });
  }
  reset() {
    this.emit('reset', { ...initComparisonMetricsState });
  }
}

class SubImpl extends Sub<IComparisonMetricsState> {
  protected actionHandlers(): Observable<IComparisonMetricsState> {
    return merge(this.passThrough(), this.fetchHandler());
  }
  private passThrough(): Observable<IComparisonMetricsState> {
    return this.actionListener([
      'toggleVisible',
      'addDate',
      'changeDate',
      'removeDate',
      'setContext',
      'reset',
    ]);
  }
  private fetchHandler(): Observable<IComparisonMetricsState> {
    return this.actionListener([
      'toggleVisible',
      'addDate',
      'changeDate',
      'removeDate',
      'setContext',
    ]).pipe(
      switchMap((state) => {
        const { visible, dates, customerId, articleIds } = state;
        const shouldFetch = visible && dates.length > 0 && !!customerId && articleIds.length > 0;
        if (!shouldFetch) {
          // nothing to show — drop any leftover metrics from a previous context
          if (!Object.keys(this.stream$.getValue().metricsByKey).length) return EMPTY;
          const cleared: IComparisonMetricsState = {
            ...this.stream$.getValue(),
            metricsByKey: {},
            action: 'fetched',
          };
          this.stream$.next(cleared);
          return of(cleared);
        }
        orderService.pub.comparisonMetrics({ customerId: customerId!, articleIds, dates });
        return orderService.sub.comparisonMetrics().pipe(
          responseHandler<IComparisonMetricsByKey>({ errorReturnType: {} }),
          map((metricsByKey) => {
            const next: IComparisonMetricsState = {
              ...this.stream$.getValue(),
              metricsByKey,
              action: 'fetched',
            };
            this.stream$.next(next);
            return next;
          }),
        );
      }),
    );
  }
}

class ComparisonMetricsState extends State<IComparisonMetricsState> {
  pub = new PubImpl(this.stream$);
  sub = new SubImpl(this.stream$);
}

export const comparisonMetricsState = new ComparisonMetricsState(initComparisonMetricsState);
